import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, trackEvent } from "@/lib/api";

const ChatCtx = createContext(null);
export const useChat = () => useContext(ChatCtx);

export function ChatProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState(null);
  const [lastCategory, setLastCategory] = useState(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [settings, setSettings] = useState({ whatsapp_number: "5571983670243", ga_id: "", pixel_id: "" });

  useEffect(() => {
    api
      .get("/settings/public")
      .then(({ data }) => {
        setSettings((s) => ({ ...s, ...data }));
        if (data.ga_id && !document.getElementById("ga-script")) {
          const s = document.createElement("script");
          s.id = "ga-script";
          s.async = true;
          s.src = `https://www.googletagmanager.com/gtag/js?id=${data.ga_id}`;
          document.head.appendChild(s);
          window.dataLayer = window.dataLayer || [];
          window.gtag = function () { window.dataLayer.push(arguments); };
          window.gtag("js", new Date());
          window.gtag("config", data.ga_id);
        }
        if (data.pixel_id && !document.getElementById("fb-pixel")) {
          const s = document.createElement("script");
          s.id = "fb-pixel";
          s.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${data.pixel_id}');fbq('track','PageView');`;
          document.head.appendChild(s);
        }
      })
      .catch(() => {});
  }, []);

  const track = useCallback((event, params) => {
    try {
      window.gtag && window.gtag("event", event, params || {});
    } catch (e) {}
    try {
      window.fbq && window.fbq("trackCustom", event, params || {});
    } catch (e) {}
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    if (open) {
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
    } else {
      html.style.overflow = "";
      body.style.overflow = "";
    }
    return () => {
      html.style.overflow = "";
      body.style.overflow = "";
    };
  }, [open]);

  const waLink = useCallback(
    (text) => `https://wa.me/${(settings.whatsapp_number || "").replace(/\D/g, "")}?text=${encodeURIComponent(text)}`,
    [settings.whatsapp_number]
  );

  const waDefault = useCallback(() => {
    const msgs = {
      geral: "Olá, Isabela! Quero realizar meu sonho com você!",
      imovel: "Olá, Isabela! Quero realizar o sonho do meu imóvel com você!",
      veiculo: "Olá, Isabela! Quero realizar o sonho do meu veículo com você!",
    };
    return waLink(msgs[lastCategory] || msgs.geral);
  }, [lastCategory, waLink]);

  const openChat = useCallback(
    (category) => {
      setPreset(category || null);
      if (category) setLastCategory(category);
      setOpen(true);
      track("chat_open", { category: category || "geral" });
      trackEvent("chat_opened", { category: category || "geral" });
    },
    [track]
  );

  useEffect(() => {
    if (!sessionStorage.getItem("ig_pageview")) {
      sessionStorage.setItem("ig_pageview", "1");
      trackEvent("pageview");
    }
    const handler = (e) => {
      const el = e.target && e.target.closest ? e.target.closest("[data-testid]") : null;
      if (!el) return;
      const tid = el.getAttribute("data-testid") || "";
      const origins = {
        "whatsapp-float-button": "float",
        "nav-whatsapp-button": "nav",
        "nav-mobile-whatsapp-button": "nav",
        "footer-whatsapp-link": "footer",
        "about-whatsapp-button": "about",
        "final-whatsapp-button": "final",
        "chat-result-whatsapp-button": "chat_result",
        "chat-result-schedule-button": "chat_result",
      };
      if (origins[tid]) trackEvent("wa_click", { origin: origins[tid] });
      if (tid.includes("imovel") && (tid.endsWith("-button") || tid.endsWith("-cta"))) trackEvent("cta_click", { category: "imovel" });
      if (tid.includes("veiculo") && (tid.endsWith("-button") || tid.endsWith("-cta"))) trackEvent("cta_click", { category: "veiculo" });
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const trackLead = useCallback(
    (category) => {
      track("generate_lead", { category: category || "geral" });
      try {
        window.fbq && window.fbq("track", "Lead", { content_category: category || "geral" });
      } catch (e) {}
    },
    [track]
  );

  return (
    <ChatCtx.Provider
      value={{
        open,
        setOpen,
        preset,
        openChat,
        settings,
        waLink,
        waDefault,
        track,
        trackLead,
        privacyOpen,
        setPrivacyOpen,
      }}
    >
      {children}
    </ChatCtx.Provider>
  );
}
